import { useTranslation } from "react-i18next";
import { Badge } from "@h2j-data-team/ui";
import type { CapacitySnapshot, CapacitySource } from "@repo/shared";

import { useFormatDate } from "@/hooks/use-format-date";
import { formatBytes } from "@/lib/health-format";

// Fixed reading order (and colors) for the segmented bar and its legend, so the
// same origin always sits in the same place across collections.
const SOURCE_ORDER: CapacitySource[] = ["datafile", "app_import", "app_reload"];
const SOURCE_COLOR: Record<CapacitySource, string> = {
  datafile: "bg-chart-1",
  app_import: "bg-chart-2",
  app_reload: "bg-chart-3",
};

/**
 * The tenant's live Data-for-Analysis aggregate: month-to-date usage against
 * the capacity limit, split by origin. The bar's track IS the limit, so the
 * empty part of it is the remaining headroom.
 */
export function CapacityGaugeCard({ snapshot }: { snapshot: CapacitySnapshot }) {
  const { t } = useTranslation("capacity");
  const { formatDate, formatDateTime } = useFormatDate();

  const usage = snapshot.localUsageBytes ?? 0;
  const limit = snapshot.capacityLimitBytes ?? 0;
  const segmentTotal = snapshot.segments.reduce((sum, s) => sum + s.bytes, 0);
  // Against the limit when Qlik reports one; otherwise the bar just shows the
  // origin split of what was consumed.
  const scale = limit > 0 ? limit : Math.max(segmentTotal, usage);
  const usagePct = limit > 0 ? (usage / limit) * 100 : null;
  const legendTotal = segmentTotal > 0 ? segmentTotal : usage;

  const segments = SOURCE_ORDER.map((source) => ({
    source,
    bytes: snapshot.segments.find((s) => s.source === source)?.bytes ?? 0,
  })).filter((s) => s.bytes > 0);

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface px-5 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-faint">
            {t("gauge.eyebrow")}
          </p>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-[34px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-ink">
              {formatBytes(usage)}
            </span>
            {limit > 0 && (
              <span className="text-[13px] text-dim">
                {t("gauge.ofLimit", { limit: formatBytes(limit) })}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-[12px] text-faint">
            {usagePct === null
              ? t("gauge.noLimit")
              : t("gauge.pctOfLimit", { pct: usagePct.toFixed(1) })}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          {snapshot.closeToOverage && <Badge variant="warning">{t("gauge.closeToOverage")}</Badge>}
          {snapshot.globalPeakBytes !== null && (
            <span className="text-[12px] text-dim">
              {snapshot.globalPeakDate
                ? t("gauge.peakOn", {
                    size: formatBytes(snapshot.globalPeakBytes),
                    // Date-only value: pin it to local midnight so a UTC parse
                    // doesn't shift it to the previous day.
                    date: formatDate(`${snapshot.globalPeakDate}T00:00:00`),
                  })
                : t("gauge.peak", { size: formatBytes(snapshot.globalPeakBytes) })}
            </span>
          )}
        </div>
      </div>

      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-surface-sunken">
        {segments.map((s) => (
          <div
            key={s.source}
            className={SOURCE_COLOR[s.source]}
            // A tiny origin (a few MB against a TB limit) must still be visible,
            // hence the floor — it only ever borrows from the empty headroom.
            style={{ width: `${scale > 0 ? (s.bytes / scale) * 100 : 0}%`, minWidth: "4px" }}
            title={`${t(`source.${s.source}`)} · ${formatBytes(s.bytes)}`}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {segments.map((s) => (
          <span key={s.source} className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${SOURCE_COLOR[s.source]}`} />
            <span className="text-[12.5px] text-dim">{t(`source.${s.source}`)}</span>
            <span className="font-mono text-[12.5px] tabular-nums text-ink">
              {formatBytes(s.bytes)}
            </span>
            <span className="text-[12px] text-faint">
              {legendTotal > 0 ? `${Math.round((s.bytes / legendTotal) * 100)}%` : "—"}
            </span>
          </span>
        ))}
      </div>

      <p className="border-t border-hairline-soft pt-3 text-[11.5px] text-faint">
        {snapshot.qlikUpdateTime
          ? t("gauge.updated", { when: formatDateTime(snapshot.qlikUpdateTime) })
          : t("gauge.neverUpdated")}
      </p>
    </div>
  );
}
